import { useState } from "react";
import { useDataProvider, useNotify, useRefresh, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import type { CrmDataProvider } from "../providers/types";
import { EXPENSE_MESSAGES } from "./expenseRules";
import type { Expense } from "./expenseTypes";

/**
 * "Cancelar gasto": an expense is never deleted, it is cancelled with a
 * MANDATORY reason (the row stays visible as Cancelado and leaves the totals).
 */
export const ExpenseCancelDialog = ({
  expense,
  open,
  onClose,
}: {
  expense: Expense;
  open: boolean;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [isCancelling, setIsCancelling] = useState(false);

  const close = () => {
    setReason("");
    setError(undefined);
    onClose();
  };

  const handleConfirm = async () => {
    if (reason.trim() === "") {
      setError(EXPENSE_MESSAGES.cancelReasonRequired);
      return;
    }
    setIsCancelling(true);
    try {
      await dataProvider.cancelExpense(expense.id, reason);
      notify("crm.expenses.cancel.done");
      refresh();
      close();
    } catch (cancelError) {
      setError(
        cancelError instanceof Error && cancelError.message
          ? cancelError.message
          : translate("crm.expenses.save_error"),
      );
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{translate("crm.expenses.cancel.title")}</DialogTitle>
          <DialogDescription>
            {translate("crm.expenses.cancel.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label htmlFor="expense-cancel-reason">
            {translate("crm.expenses.cancel.reason")}
          </Label>
          <Textarea
            id="expense-cancel-reason"
            value={reason}
            disabled={isCancelling}
            aria-invalid={error != null}
            onChange={(event) => {
              setReason(event.target.value);
              setError(undefined);
            }}
          />
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            className="min-h-11"
            disabled={isCancelling}
            onClick={close}
          >
            {translate("crm.expenses.cancel.back")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="min-h-11"
            disabled={isCancelling}
            onClick={() => void handleConfirm()}
          >
            {translate("crm.expenses.cancel.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
